import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, CheckCircle2, FileDown, Stethoscope, FolderOpen, ChevronDown, ChevronRight, ClipboardList, StickyNote, FileText, Plus, Trash2, CalendarDays, Phone, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError, API } from "@/lib/api";
import { InsuranceChips } from "@/components/InsurancesEditor";

const HISTORY_LABELS = {
  motivo: "Motivo", hallazgos: "Hallazgos", diagnostico: "Diagnóstico",
  plan_tratamiento: "Plan", alergias: "Alergias", receta: "Receta",
};

const KIND_LABEL = { consultation: "Consulta", file: "Archivo", note: "Nota" };
function KindIcon({ kind, className = "h-3.5 w-3.5" }) {
  if (kind === "consultation") return <ClipboardList className={className} />;
  if (kind === "file") return <FileText className={className} />;
  return <StickyNote className={className} />;
}
const newId = () => Math.random().toString(36).slice(2);
const EMPTY_FORM = { motivo: "", hallazgos: "", diagnostico: "", plan_tratamiento: "", alergias: "", receta: "" };

const FIELDS = [
  ["motivo", "Motivo de consulta", "¿Por qué acude el paciente?"],
  ["hallazgos", "Hallazgos", "Examen físico, signos vitales, observaciones…"],
  ["diagnostico", "Diagnóstico", "Diagnóstico principal y secundarios"],
  ["plan_tratamiento", "Plan de tratamiento", "Indicaciones, estudios, seguimiento…"],
  ["alergias", "Alergias", "Alergias conocidas del paciente"],
  ["receta", "Receta / Rp.", "Medicamentos, dosis y frecuencia"],
];

export default function Consultation() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [appt, setAppt] = useState(null);
  const [consultId, setConsultId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [meds, setMeds] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [insurances, setInsurances] = useState([]);

  useEffect(() => {
    api.get("/doctors/me/insurances").then((r) => setInsurances(r.data.insurances || [])).catch(() => {});
  }, []);

  const parseMeds = (txt) => (txt || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const p = l.replace(/^•\s*/, "").split(" — ");
    return { _id: newId(), medicamento: p[0] || "", dosis: p[1] || "", indicaciones: p.slice(2).join(" — ") || "" };
  });
  const composeMeds = (arr) => arr.filter((m) => (m.medicamento || "").trim()).map((m) => {
    const parts = ["• " + m.medicamento.trim()];
    if ((m.dosis || "").trim()) parts.push(m.dosis.trim());
    if ((m.indicaciones || "").trim()) parts.push(m.indicaciones.trim());
    return parts.join(" — ");
  }).join("\n");

  useEffect(() => { setForm((f) => ({ ...f, receta: composeMeds(meds) })); /* eslint-disable-next-line */ }, [meds]);

  const addMed = () => setMeds((m) => [...m, { _id: newId(), medicamento: "", dosis: "", indicaciones: "" }]);
  const updateMed = (i, k) => (e) => setMeds((arr) => arr.map((m, idx) => (idx === i ? { ...m, [k]: e.target.value } : m)));
  const removeMed = (i) => setMeds((arr) => arr.filter((_, idx) => idx !== i));

  useEffect(() => {
    setAppt(null);
    setConsultId(null);
    setForm(EMPTY_FORM);
    setMeds([]);
    setHistory([]);
    setPatientInfo(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      try {
        const a = await api.get(`/appointments/${appointmentId}`);
        setAppt(a.data);
        const c = await api.get(`/consultations/by-appointment/${appointmentId}`);
        if (c.data && c.data.id) {
          setConsultId(c.data.id);
          setForm({
            motivo: c.data.motivo || "", hallazgos: c.data.hallazgos || "",
            diagnostico: c.data.diagnostico || "", plan_tratamiento: c.data.plan_tratamiento || "",
            alergias: c.data.alergias || "", receta: c.data.receta || "",
          });
          setMeds(parseMeds(c.data.receta || ""));
        }
        const pid = a.data.patient_id;
        const [cons, recs] = await Promise.all([
          api.get("/consultations", { params: { patient_id: pid } }),
          api.get("/records", { params: { patient_id: pid } }),
        ]);
        const tl = [
          ...cons.data.filter((x) => x.appointment_id !== appointmentId).map((x) => ({ ...x, _kind: "consultation" })),
          ...recs.data.map((r) => ({ ...r, _kind: r.type })),
        ].sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));
        setHistory(tl);

        try {
          const { data: pinfo } = await api.get(`/patients/${a.data.patient_id}/info`);
          setPatientInfo(pinfo);
        } catch (e) { console.error("No se pudo cargar la info del paciente:", e); }
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
        navigate("/panel");
      }
    })();
  }, [appointmentId, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const persist = async (finalize) => {
    const { data } = await api.post("/consultations", {
      appointment_id: appointmentId, patient_id: appt?.patient_id, ...form, finalize,
    });
    setConsultId(data.id);
    setForm((f) => ({
      ...f, motivo: data.motivo || "", hallazgos: data.hallazgos || "", diagnostico: data.diagnostico || "",
      plan_tratamiento: data.plan_tratamiento || "", alergias: data.alergias || "",
    }));
    return data;
  };

  const save = async () => {
    setBusy(true);
    try { await persist(false); toast.success("Consulta guardada"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const findNextArrived = async () => {
    const { data } = await api.get("/appointments/doctor", { params: { date: appt.date } });
    return data
      .filter((x) => x.id !== appointmentId && x.center === appt.center && x.status === "arrived")
      .sort((x, y) => (x.turn_number || 9999) - (y.turn_number || 9999))[0] || null;
  };

  const finalize = async () => {
    setBusy(true);
    try {
      await persist(true);
      const next = await findNextArrived();
      if (!next) {
        toast.success("Consulta finalizada. No hay más pacientes en espera.");
        navigate("/panel");
        return;
      }
      await api.patch(`/appointments/${next.id}/start`);
      toast.success(`Consulta finalizada. Llamando turno ${next.turn_number}: ${next.patient_name}`);
      navigate(`/panel/consulta/${next.id}`, { replace: true });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const exportPdf = async () => {
    setBusy(true);
    const win = window.open("", "_blank");
    try {
      const data = await persist(false);
      const url = `${API}/consultations/${data.id}/receta.pdf?v=${encodeURIComponent(data.updated_at || Date.now())}`;
      if (win) win.location.href = url; else window.open(url, "_blank");
    } catch (err) { if (win) win.close(); toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/panel")} className="mb-4 flex items-center gap-1.5 text-sm font-bold text-slate-500" data-testid="consult-back">
        <ArrowLeft className="h-4 w-4" /> Volver al panel
      </button>

      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-50 text-[#0284C7]"><Stethoscope className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Atención de consulta</h1>
          <p className="text-sm font-semibold text-slate-500" data-testid="consult-patient">
            {appt ? `${appt.patient_name} · ${appt.date}${appt.turn_number ? ` · Turno ${appt.turn_number}` : ""}` : "Cargando…"}
          </p>
        </div>
      </div>

      {/* Datos del paciente */}
      {(patientInfo || appt?.is_walk_in) && (
        <div className="mt-5 grid gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:grid-cols-3" data-testid="patient-info-card">
          <InfoItem icon={CalendarDays} label="Fecha de nacimiento" value={fmtBirth(patientInfo?.birthdate)} />
          <InfoItem icon={ShieldAlert} label="Contacto de emergencia" value={
            patientInfo?.emergency_contact_name
              ? `${patientInfo.emergency_contact_name}${patientInfo.emergency_contact_phone ? ` · ${patientInfo.emergency_contact_phone}` : ""}`
              : "—"
          } />
          <InfoItem icon={Phone} label="Teléfono" value={patientInfo?.phone || appt?.walk_in_phone || "—"} />
        </div>
      )}

      {/* Expediente del paciente */}
      <div className="mt-5 overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white" data-testid="patient-expediente">
        <button onClick={() => setShowHistory((s) => !s)} className="flex w-full items-center justify-between px-5 py-3 hover:bg-slate-50" data-testid="expediente-toggle">
          <span className="flex items-center gap-2 text-sm font-extrabold text-slate-900"><FolderOpen className="h-4 w-4 text-[#0284C7]" /> Expediente del paciente <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{history.length}</span></span>
          {showHistory ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </button>
        {showHistory && (
          <div className="max-h-72 overflow-y-auto border-t border-[#E2E8F0] bg-slate-50/50 p-4">
            {history.length === 0 && <p className="text-sm font-bold text-slate-400">Sin registros clínicos previos.</p>}
            <div className="space-y-2.5">
              {history.map((item) => (
                <div key={item.id} className="rounded-xl border border-[#E2E8F0] bg-white p-3" data-testid={`exp-${item.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
                      <KindIcon kind={item._kind} />
                      {KIND_LABEL[item._kind] || "Nota"}
                    </span>
                    <span className="text-xs font-bold text-slate-400">{(item.created_at || "").slice(0, 10)}</span>
                  </div>
                  {item._kind === "consultation" ? (
                    <div className="mt-1.5 space-y-1">
                      {Object.keys(HISTORY_LABELS).map((k) => item[k] ? (
                        <div key={k} className="text-xs"><span className="font-bold text-[#0284C7]">{HISTORY_LABELS[k]}:</span> <span className="font-semibold text-slate-600">{item[k]}</span></div>
                      ) : null)}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-xs font-semibold text-slate-600">{item.note_text || item.original_filename}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="consult-insurances">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Seguros aceptados</div>
        <InsuranceChips items={insurances} testid="consult-insurance-list" />
      </div>

      <div className="mt-6 space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-6">
        {FIELDS.map(([key, label, ph]) => (
          key === "receta" ? (
            <div key={key}>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
              <div className="mt-1.5 space-y-2" data-testid="med-builder">
                {meds.length === 0 && <p className="text-xs font-semibold text-slate-400">Sin medicamentos. Agrega uno con el botón de abajo.</p>}
                {meds.map((m, i) => (
                  <div key={m._id || i} className="grid gap-2 rounded-xl border border-[#E2E8F0] bg-slate-50/50 p-3 sm:grid-cols-[1fr_1fr_1.4fr_auto]" data-testid={`med-row-${i}`}>
                    <Input placeholder="Medicamento" value={m.medicamento} onChange={updateMed(i, "medicamento")} className="rounded-xl border-[#E2E8F0] bg-white" data-testid={`med-name-${i}`} />
                    <Input placeholder="Dosis" value={m.dosis} onChange={updateMed(i, "dosis")} className="rounded-xl border-[#E2E8F0] bg-white" data-testid={`med-dose-${i}`} />
                    <Input placeholder="Indicaciones" value={m.indicaciones} onChange={updateMed(i, "indicaciones")} className="rounded-xl border-[#E2E8F0] bg-white" data-testid={`med-instructions-${i}`} />
                    <button type="button" onClick={() => removeMed(i)} className="flex items-center justify-center rounded-xl border border-[#E2E8F0] bg-white px-3 text-red-400 hover:bg-red-50" data-testid={`med-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addMed} className="rounded-full border-[#E2E8F0] font-bold text-[#0284C7]" data-testid="add-med-btn">
                  <Plus className="mr-1.5 h-4 w-4" /> Agregar medicamento
                </Button>
              </div>
            </div>
          ) : (
            <div key={key}>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
              <Textarea value={form[key]} onChange={set(key)} placeholder={ph} className="mt-1.5 min-h-20 rounded-xl border-[#E2E8F0]" data-testid={`consult-${key}`} />
            </div>
          )
        ))}
      </div>

      <div className="sticky bottom-4 mt-6 flex flex-wrap gap-3 rounded-2xl border border-[#E2E8F0] bg-white/90 p-4 backdrop-blur">
        <Button variant="outline" onClick={exportPdf} disabled={busy} className="rounded-full border-[#E2E8F0] font-bold" data-testid="export-receta-btn">
          <FileDown className="mr-1.5 h-4 w-4" /> Exportar receta PDF
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={save} disabled={busy} className="rounded-full border-[#E2E8F0] font-bold" data-testid="save-consult-btn">
          <Save className="mr-1.5 h-4 w-4" /> Guardar
        </Button>
        <Button onClick={finalize} disabled={busy} className="rounded-full bg-emerald-500 font-bold hover:bg-emerald-600" data-testid="finalize-consult-btn">
          <CheckCircle2 className="mr-1.5 h-4 w-4" /> Finalizar y cerrar
        </Button>
      </div>

    </div>
  );
}

function fmtBirth(b) {
  if (!b) return "—";
  const d = new Date(b + "T00:00:00");
  if (isNaN(d.getTime())) return b;
  const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  return `${b} · ${age} años`;
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#0284C7]" />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-sm font-bold text-slate-800">{value}</div>
      </div>
    </div>
  );
}
